import React, {
    forwardRef,
    useState,
    useEffect,
    useRef,
    useCallback,
} from 'react'
import { Box, Alert } from '@mui/material'
import { embedDashboard } from '@superset-ui/embedded-sdk'
import { BaseBlockProps } from '../types'
import { LoadingState } from '../base'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SupersetBlockProps extends BaseBlockProps {}

const SUPERSET_DOMAIN = 'https://superset.kedo.nu'

export const SupersetBlock = forwardRef<HTMLDivElement, SupersetBlockProps>(
    ({ kedo, block }, ref) => {
        const [loading, setLoading] = useState(true)
        const [error, setError] = useState<string | null>(null)

        const containerRef = useRef<HTMLDivElement>(null)
        const dashboardRef = useRef<{ unmount?: () => void } | null>(null)

        const dashboardId =
            block.settings?.dashboardId ?? block.settings?.chart_identifier

        const fetchGuestToken = useCallback(async (): Promise<string> => {
            try {
                // New backend contract
                if (block.settings?.dashboardId && block.settings?.authToken) {
                    const response = await kedo
                        .api()
                        .post('/superset/guest-token', {
                            dashboardId: block.settings.dashboardId,
                            authToken: block.settings.authToken,
                        })

                    if (!response.data?.token) {
                        throw new Error('No token returned from backend')
                    }

                    return response.data.token
                }

                // Backward-compatible old contract
                const response = await kedo
                    .api()
                    .get(
                        `/superset-token?chartLayout=${block.settings?.chart_layout}&chartId=${block.settings?.chart_identifier}`
                    )

                if (!response.data?.token) {
                    throw new Error('No token returned from backend')
                }

                return response.data.token
            } catch (err: any) {
                throw new Error(
                    err?.response?.data?.message ||
                        err?.message ||
                        'Failed to fetch guest token'
                )
            }
        }, [kedo, block.settings])

        const initDashboard = useCallback(async () => {
            if (!containerRef.current || !dashboardId) {
                setError('Missing dashboard id in block.settings')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                // Clean previous mount
                dashboardRef.current?.unmount?.()
                containerRef.current.innerHTML = ''

                dashboardRef.current = await embedDashboard({
                    id: String(dashboardId),
                    supersetDomain: SUPERSET_DOMAIN,
                    mountPoint: containerRef.current,
                    fetchGuestToken,
                    dashboardUiConfig: {
                        filters: {
                            expanded: false,
                        },
                        hideTitle: true,
                        hideChartControls: true,
                        hideTab: true,
                    },
                })
            } catch (err: any) {
                console.error('Failed to embed Superset dashboard:', err)
                setError(err?.message || 'Could not load dashboard')
            } finally {
                setLoading(false)
            }
        }, [dashboardId, fetchGuestToken])

        useEffect(() => {
            initDashboard()

            return () => {
                dashboardRef.current?.unmount?.()
            }
        }, [initDashboard])

        return (
            <Box
                ref={ref}
                sx={{
                    height: '100vh',
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {loading && <LoadingState />}

                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    ref={containerRef}
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: loading || !!error ? 'none' : 'block',
                        '& iframe': {
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        },
                    }}
                />
            </Box>
        )
    }
)

SupersetBlock.displayName = 'SupersetBlock'
